/* Studentskylt v2.5 — add as assets/studentskylt-editor.js.
 * Uses the existing theme product form and its cart handling.
 * Free photo movement through all SVG transparency; writes _Skyltdata v3.
 * Use the updated production tool for these orders.
 */
(() => {
  'use strict';
  if (customElements.get('sf-sign-editor')) return;
  const NS = 'http://www.w3.org/2000/svg';
  const round = (n, places = 3) => Number(n.toFixed(places));
  const compact = n => Number(n.toPrecision(10));

  class SignEditor extends HTMLElement {
    connectedCallback() {
      clearTimeout(this.initTimer);
      this.initTimer = setTimeout(() => this.init(), 0);
    }

    disconnectedCallback() {
      clearTimeout(this.initTimer);
      clearTimeout(this.thumbTimer);
      this.events?.abort();
      this.cartObserver?.disconnect();
      this.loadToken = (this.loadToken || 0) + 1;
      this.revision = (this.revision || 0) + 1;
      if (this.thumbURL) URL.revokeObjectURL(this.thumbURL);
      this.thumbURL = null;
      this.pointers?.clear();
      this.initialized = false;
      delete this.dataset.ready;
      this.busy = false;
      this.inert = false;
    }

    init() {
      if (!this.isConnected || this.initialized) return;
      this.$ = selector => this.querySelector(selector);
      this.events = new AbortController();
      this.on = (node, type, handler, options = {}) => node?.addEventListener(type, handler, {
        ...options, signal: this.events.signal
      });
      try {
        this.cfg = JSON.parse(this.$('[data-config]').textContent);
        if (!this.dataset.formId || !Number.isFinite(this.cfg.width) || !Number.isFinite(this.cfg.height) ||
            !(this.cfg.width > 0) || !(this.cfg.height > 0)) {
          throw new Error('Anpassningen saknar formulär eller mallinställningar. Kontakta butiken.');
        }
        this.svg = this.$('[data-preview]');
        this.file = this.$('[data-file]');
        this.nameInput = this.$('[data-name]');
        this.classInput = this.$('[data-class]');
        this.photo = null;
        this.photoFile = null;
        this.photoData = null;
        this.originalSaved = null;
        this.previewSaved = null;
        this.busy = false;
        this.revision = (this.revision || 0) + 1;
        this.loadToken = (this.loadToken || 0) + 1;
        this.pointers = new Map();
        this.initialized = true;
        this.prepareSVG();
        this.prepareControls();
        this.resetPosition();
        this.on(this.file, 'change', () => this.loadPhoto());
        for (const selector of ['[data-name]', '[data-class]']) {
          this.on(this.$(selector), 'input', () => { if (!this.busy) this.changed(); });
        }
        this.on(this.$('[data-reset]'), 'click', () => {
          if (this.busy) return;
          this.resetPosition();
          this.changed();
        });
        this.on(document, 'submit', event => this.submit(event), {capture: true});
        this.on(window, 'pageshow', () => this.release());
        this.installGestures();
        this.update();
        this.dataset.ready = 'true';
        if (this.file.files?.length) this.loadPhoto();
      } catch (error) {
        this.initialized = false;
        delete this.dataset.ready;
        this.error(error.message || 'Anpassningen kunde inte startas. Kontakta butiken.');
        // Fail closed if initialization fails: never sell an unconfigured sign.
        this.on(document, 'submit', event => {
          if (event.target.id !== this.dataset.formId) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.$('[data-error]')?.focus();
        }, {capture: true});
      }
    }

    form() { return document.getElementById(this.dataset.formId); }
    attrs(node, values) {
      for (const [key, value] of Object.entries(values)) node.setAttribute(key, String(value));
    }
    error(message) { if (this.$('[data-error]')) this.$('[data-error]').textContent = message; }
    status(message) { if (this.$('[data-status]')) this.$('[data-status]').textContent = message; }

    prepareSVG() {
      const page = {x: 0, y: 0, width: this.cfg.width, height: this.cfg.height};
      // Fit the embedded SVG's viewBox into the editor, keeping its drawing coordinates intact.
      const artwork = this.$('[data-artwork]')?.querySelector('svg');
      if (artwork) this.attrs(artwork, {...page, preserveAspectRatio: 'xMidYMid meet'});
      // Remove only the editor's old aperture, never masks/clips inside the SVG artwork.
      const photoLayer = this.$('[data-photo]').parentElement;
      photoLayer.removeAttribute('clip-path');
      photoLayer.style.removeProperty('clip-path');
      this.$('[data-clip]')?.remove();
      const background = photoLayer.querySelector('rect');
      if (background) this.attrs(background, {...page, fill: '#ffffff'});
      this.svg.setAttribute('overflow', 'hidden');
      this.svg.style.overflow = 'hidden';
      this.$('[data-touch-surface]')?.remove();
      this.touchSurface = document.createElementNS(NS, 'rect');
      this.attrs(this.touchSurface, {...page, 'data-touch-surface': '', fill: 'transparent',
        role: 'img', tabindex: '-1',
        'aria-label': 'Flytta fotot med piltangenterna. Zooma med plus och minus. Home centrerar bilden.'});
      this.touchSurface.style.touchAction = 'none';
      this.touchSurface.style.cursor = 'grab';
      // The entire sign captures gestures, including transparent areas outside the old aperture.
      this.svg.append(this.touchSurface);
      for (const key of ['name', 'class']) {
        const node = this.$(`[data-${key}-preview]`);
        this.attrs(node, {x: this.cfg[key].x, y: this.cfg[key].y, 'text-anchor': 'middle'});
        Object.assign(node.style, {
          fontFamily: this.cfg[key].font || this.cfg.font,
          fontWeight: String(this.cfg[key].weight ?? (key === 'name' ? 700 : 400)),
          fill: this.cfg[key].color || this.cfg.textColor,
          letterSpacing: `${this.cfg[key].letterSpacing ?? 0}px`, textTransform: 'none'
        });
      }
    }

    prepareControls() {
      // Upgrade existing Liquid markup too: only this JS asset needs replacing.
      const adjust = this.$('[data-adjust]');
      const reset = this.$('[data-reset]');
      adjust.replaceChildren(reset);
      const help = this.$('[data-gesture-help]') || adjust.previousElementSibling;
      if (help?.tagName === 'P') help.textContent =
        'Dra fotot för att flytta det. Nyp med två fingrar eller använd mushjulet för att zooma.';
      // Remove the old required approval field and its label from existing snippets.
      // Do not submit an approval value when the customer has not been asked to approve.
      const approval = this.$('[data-confirm]');
      if (approval) (approval.closest('label') || approval).remove();
    }

    resetPosition() {
      this.transform = {z: 1, x: 0, y: 0};
    }

    changed() {
      this.revision += 1;
      this.previewSaved = null;
      for (const selector of ['[data-preview-property]', '[data-preview-link-property]']) this.$(selector).value = '';
      this.error('');
      this.status('');
      this.update();
    }

    async loadPhoto() {
      if (this.busy) return;
      const token = ++this.loadToken;
      this.photo = this.photoFile = this.photoData = this.originalSaved = null;
      this.$('[data-photo]').removeAttribute('href');
      this.$('[data-original-property]').value = '';
      this.changed();
      const file = this.file.files[0];
      const fileName = this.$('[data-file-name]');
      if (fileName) fileName.textContent = file ? file.name : 'Ingen bild vald';
      if (!file) return;
      this.status('Öppnar ditt foto …');
      let url;
      try {
        if (file.size > 10 * 1024 * 1024) throw new Error('Bilden är större än 10 MB. Välj en mindre JPG- eller PNG-fil.');
        const bytes = new Uint8Array(await file.slice(0, 8).arrayBuffer());
        const jpg = bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
        const png = [137, 80, 78, 71, 13, 10, 26, 10].every((n, i) => bytes[i] === n);
        if (!jpg && !png) throw new Error('Välj en JPG- eller PNG-bild. Spara HEIC-bilder som JPG först.');
        url = URL.createObjectURL(file);
        const picture = new Image();
        picture.src = url;
        await picture.decode();
        if (!this.isConnected || token !== this.loadToken) return;
        if (!picture.naturalWidth || picture.naturalWidth * picture.naturalHeight > 24000000) {
          throw new Error('Bilden är för stor att bearbeta här. Spara en kopia med högst 24 megapixlar.');
        }
        // Normalize EXIF orientation for the SVG preview; the original upload stays intact.
        const canvas = document.createElement('canvas');
        const factor = Math.min(1, 2400 / Math.max(picture.naturalWidth, picture.naturalHeight));
        canvas.width = Math.max(1, Math.round(picture.naturalWidth * factor));
        canvas.height = Math.max(1, Math.round(picture.naturalHeight * factor));
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Webbläsaren kunde inte öppna bilden.');
        context.drawImage(picture, 0, 0, canvas.width, canvas.height);
        const data = canvas.toDataURL(png ? 'image/png' : 'image/jpeg', 0.94);
        canvas.width = canvas.height = 1;
        if (!data.startsWith('data:image/')) throw new Error('Bilden kunde inte öppnas. Prova en mindre fil.');
        this.photo = picture;
        this.photoFile = file;
        this.photoData = data;
        this.$('[data-photo]').setAttribute('href', data);
        this.resetPosition();
        this.changed();
      } catch (error) {
        if (!this.isConnected || token !== this.loadToken) return;
        this.file.value = '';
        if (fileName) fileName.textContent = 'Ingen bild vald';
        this.status('');
        this.error(error.name === 'Error' ? error.message : 'Bilden kunde inte öppnas. Prova en annan JPG- eller PNG-fil.');
        this.update();
      } finally {
        if (url) URL.revokeObjectURL(url);
      }
    }

    positionGeometry(z, panX, panY) {
      const p = {x: 0, y: 0, width: this.cfg.width, height: this.cfg.height};
      const scale = Math.max(p.width / this.photo.naturalWidth, p.height / this.photo.naturalHeight) * z;
      const width = this.photo.naturalWidth * scale, height = this.photo.naturalHeight * scale;
      return {
        x: p.x + (p.width - width) / 2 + p.width * (panX / 100),
        y: p.y + (p.height - height) / 2 + p.height * (panY / 100),
        width, height, scale, z, panX, panY
      };
    }

    geometry() {
      const {z, x, y} = this.transform;
      return this.positionGeometry(compact(z), compact(x), compact(y));
    }

    validGeometry(g) {
      return Object.values(g).every(Number.isFinite) && g.z > 0 && g.width > 0 && g.height > 0 && g.scale > 0;
    }

    fitText(key, value, fallback) {
      const node = this.$(`[data-${key}-preview]`), cfg = this.cfg[key];
      node.textContent = value.trim() || fallback;
      node.removeAttribute('textLength');
      node.removeAttribute('lengthAdjust');
      node.style.fontSize = `${cfg.size}px`;
      node.setAttribute('font-size', cfg.size);
      const measured = node.getComputedTextLength();
      const size = round(measured > cfg.maxWidth ? cfg.size * cfg.maxWidth / measured : cfg.size);
      node.style.fontSize = `${size}px`;
      node.setAttribute('font-size', size);
      return size;
    }

    update() {
      const ns = this.fitText('name', this.nameInput.value, 'Ditt namn');
      const cs = this.fitText('class', this.classInput.value, 'Din klass');
      this.$('[data-placeholder]').style.display = this.photo ? 'none' : '';
      this.$('[data-adjust]').disabled = !this.photo || this.busy;
      this.touchSurface.style.pointerEvents = this.photo ? 'all' : 'none';
      this.touchSurface.setAttribute('tabindex', this.photo && !this.busy ? '0' : '-1');
      this.$('[data-quality]').textContent = '';
      this.$('[data-template-property]').value = this.cfg.template;
      if (!this.photo) {
        this.$('[data-recipe]').value = '';
        this.$('[data-thumbnail]').hidden = true;
        return;
      }
      const g = this.geometry();
      if (!this.validGeometry(g)) {
        this.$('[data-recipe]').value = '';
        this.$('[data-quality]').textContent = 'Centrera bilden för att återställa placeringen.';
        return;
      }
      this.attrs(this.$('[data-photo]'), {x: g.x, y: g.y, width: g.width, height: g.height});
      const ppi = 25.4 * this.cfg.width / (g.scale * this.cfg.printWidthMm);
      this.$('[data-quality]').textContent = ppi < 120
        ? 'Bilden kan bli oskarp i tryck. Välj gärna en större originalbild eller zooma ut.'
        : 'Kontrollera att hela ansiktet syns och att bilden är skarp.';
      this.recipe = {
        v: 3, m: this.cfg.template, z: g.z,
        x: g.panX, y: g.panY,
        w: this.photo.naturalWidth, h: this.photo.naturalHeight, ns, cs,
        nw: round(this.$('[data-name-preview]').getComputedTextLength()),
        cw: round(this.$('[data-class-preview]').getComputedTextLength()), mm: this.cfg.printWidthMm
      };
      this.$('[data-recipe]').value = JSON.stringify(this.recipe);
      clearTimeout(this.thumbTimer);
      const revision = this.revision;
      this.thumbTimer = setTimeout(() => this.updateThumbnail(revision), 350);
    }

    point(event) {
      const matrix = this.svg.getScreenCTM();
      if (!matrix) return null;
      return new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse());
    }

    setPosition(x, y, zoom) {
      const p = {width: this.cfg.width, height: this.cfg.height};
      const z = compact(zoom), centered = this.positionGeometry(z, 0, 0);
      const panX = compact(((x - centered.x) / p.width) * 100);
      const panY = compact(((y - centered.y) / p.height) * 100);
      if (!this.validGeometry(this.positionGeometry(z, panX, panY))) return;
      // No edge clamp or arbitrary zoom cap. Keep tiny positive zooms representable.
      this.transform = {z, x: panX, y: panY};
      this.changed();
    }

    beginGesture() {
      const points = [...this.pointers.values()];
      if (!points.length || !this.photo) { this.gesture = null; return; }
      const center = points.length > 1
        ? {x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2} : points[0];
      this.gesture = {
        geometry: this.geometry(), center,
        distance: points.length > 1 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0
      };
    }

    installGestures() {
      this.on(this.touchSurface, 'pointerdown', event => {
        if (this.busy || !this.photo || (event.pointerType === 'mouse' && event.button !== 0) || this.pointers.size >= 2) return;
        const point = this.point(event);
        if (!point) return;
        event.preventDefault();
        this.pointers.set(event.pointerId, point);
        this.touchSurface.setPointerCapture(event.pointerId);
        this.touchSurface.style.cursor = 'grabbing';
        this.beginGesture();
      });
      this.on(this.touchSurface, 'pointermove', event => {
        if (this.busy || !this.pointers.has(event.pointerId) || !this.gesture) return;
        const point = this.point(event);
        if (!point) return;
        event.preventDefault();
        this.pointers.set(event.pointerId, point);
        const points = [...this.pointers.values()];
        const start = this.gesture, g = start.geometry;
        if (points.length === 1) {
          this.setPosition(g.x + point.x - start.center.x, g.y + point.y - start.center.y, g.z);
        } else {
          const center = {x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2};
          const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
          if (!(distance > 0) || !(start.distance > 0)) return;
          const z = g.z * (distance / start.distance);
          this.setPosition(center.x - (start.center.x - g.x) * z / g.z,
            center.y - (start.center.y - g.y) * z / g.z, z);
        }
      });
      const end = event => {
        this.pointers.delete(event.pointerId);
        if (this.touchSurface.hasPointerCapture(event.pointerId)) this.touchSurface.releasePointerCapture(event.pointerId);
        this.touchSurface.style.cursor = this.pointers.size ? 'grabbing' : 'grab';
        this.beginGesture();
      };
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) this.on(this.touchSurface, type, end);
      this.on(this.touchSurface, 'wheel', event => {
        if (this.busy || !this.photo) return;
        const point = this.point(event);
        if (!point) return;
        event.preventDefault();
        const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 400 : 1);
        const g = this.geometry(), z = g.z * Math.exp(-delta * 0.0015);
        this.setPosition(point.x - (point.x - g.x) * z / g.z, point.y - (point.y - g.y) * z / g.z, z);
      }, {passive: false});
      this.on(this.touchSurface, 'keydown', event => {
        if (this.busy || !this.photo || event.ctrlKey || event.metaKey || event.altKey) return;
        const g = this.geometry(), step = event.shiftKey ? 10 : 1;
        const move = {ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step]}[event.key];
        const factor = ['+', '='].includes(event.key) ? 1.1 : event.key === '-' ? 1 / 1.1 : null;
        if (!move && !factor && event.key !== 'Home') return;
        event.preventDefault();
        if (move) this.setPosition(g.x + move[0], g.y + move[1], g.z);
        else if (factor) {
          const x = this.cfg.width / 2, y = this.cfg.height / 2;
          this.setPosition(x - (x - g.x) * factor, y - (y - g.y) * factor, g.z * factor);
        } else { this.resetPosition(); this.changed(); }
      });
    }

    async renderJPEG(width) {
      if (!this.photoData) throw new Error('Välj ett foto först.');
      const copy = this.svg.cloneNode(true);
      this.attrs(copy, {xmlns: NS, width, height: Math.round(width * this.cfg.height / this.cfg.width)});
      copy.removeAttribute('class');
      copy.removeAttribute('style');
      copy.querySelector('[data-placeholder]')?.remove();
      copy.querySelector('[data-touch-surface]')?.remove();
      copy.querySelector('[data-photo]').setAttribute('href', this.photoData);
      for (const key of ['name', 'class']) {
        this.attrs(copy.querySelector(`[data-${key}-preview]`), {
          textLength: this.recipe[key === 'name' ? 'nw' : 'cw'], lengthAdjust: 'spacingAndGlyphs'
        });
      }
      // External images disappear when SVG is rasterized. Require embedded artwork instead.
      for (const node of copy.querySelectorAll('image, use')) {
        const href = node.getAttribute('href') || node.getAttributeNS('http://www.w3.org/1999/xlink', 'href') || '';
        if (href && !href.startsWith('#') && !href.startsWith('data:image/')) {
          throw new Error('SVG-mallen innehåller en extern bild. Kontakta butiken så att bilden kan bäddas in.');
        }
      }
      const source = new Blob([new XMLSerializer().serializeToString(copy)], {type: 'image/svg+xml;charset=utf-8'});
      const url = URL.createObjectURL(source);
      const canvas = document.createElement('canvas');
      try {
        const picture = new Image();
        picture.src = url;
        await picture.decode();
        canvas.width = width;
        canvas.height = Math.round(width * this.cfg.height / this.cfg.width);
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Webbläsaren kunde inte skapa förhandsgranskningen.');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(picture, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
        if (!blob?.size) throw new Error('Förhandsgranskningen kunde inte sparas. Prova igen.');
        return blob;
      } finally {
        URL.revokeObjectURL(url);
        canvas.width = canvas.height = 1;
      }
    }

    async updateThumbnail(revision) {
      if (!this.photo || revision !== this.revision || !this.isConnected) return;
      try {
        const blob = await this.renderJPEG(300);
        if (revision !== this.revision || !this.isConnected) return;
        if (this.thumbURL) URL.revokeObjectURL(this.thumbURL);
        this.thumbURL = URL.createObjectURL(blob);
        this.$('[data-thumbnail]').src = this.thumbURL;
        this.$('[data-thumbnail]').hidden = false;
      } catch {
        // The full export at checkout reports any render error before adding the item.
        if (revision === this.revision) this.$('[data-thumbnail]').hidden = true;
      }
    }

    async upload(blob, filename) {
      if (!/^[a-zA-Z0-9_-]+$/.test(this.cfg.cloudName || '') || !this.cfg.uploadPreset) {
        throw new Error('Bilduppladdningen är inte inställd. Kontakta butiken.');
      }
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.events.signal.addEventListener('abort', abort, {once: true});
      if (this.events.signal.aborted || !this.isConnected) abort();
      const timer = setTimeout(abort, 45000);
      try {
        const body = new FormData();
        body.append('file', blob, filename);
        body.append('upload_preset', this.cfg.uploadPreset);
        const response = await fetch(`https://api.cloudinary.com/v1_1/${this.cfg.cloudName}/image/upload`, {
          method: 'POST', body, signal: controller.signal, credentials: 'omit'
        });
        const result = await response.json();
        if (!response.ok || !result.secure_url) throw new Error('Uppladdningen misslyckades. Kontrollera anslutningen och försök igen.');
        const url = new URL(result.secure_url);
        if (url.protocol !== 'https:' || url.hostname !== 'res.cloudinary.com' ||
            !url.pathname.startsWith(`/${this.cfg.cloudName}/image/upload/`) || url.href.length > 255) {
          throw new Error('Bildtjänsten gav en oväntad bildadress. Kontakta butiken.');
        }
        return url.href;
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('Uppladdningen tog för lång tid. Försök igen när anslutningen är stabil.');
        if (error instanceof TypeError || error instanceof SyntaxError) throw new Error('Bilden kunde inte laddas upp. Kontrollera anslutningen och försök igen.');
        throw error;
      } finally {
        clearTimeout(timer);
        this.events.signal.removeEventListener('abort', abort);
      }
    }

    lock() {
      this.busy = true;
      this.inert = true;
      this.setAttribute('aria-busy', 'true');
      this.$('[data-adjust]').disabled = true;
      this.pointers.clear();
      this.gesture = null;
    }

    release() {
      this.cartObserver?.disconnect();
      this.busy = false;
      this.inert = false;
      this.removeAttribute('aria-busy');
      if (this.$) this.$('[data-adjust]').disabled = !this.photo;
    }

    themeLoading(form, submitter) {
      const button = submitter?.isConnected ? submitter : form?.querySelector('[name="add"]');
      return button?.getAttribute('aria-disabled') === 'true' || button?.classList.contains('loading');
    }

    waitForTheme(form, submitter) {
      if (!this.forwardedEvent?.defaultPrevented) {
        // Native navigation: pageshow (including back/forward cache) releases the editor.
        return;
      }
      // aria-disabled can remain true for a newly sold-out variant after an error.
      // Dawn's loading class, unlike availability, always clears in its finally block.
      const working = () => {
        const button = submitter?.isConnected ? submitter : this.form()?.querySelector('[name="add"]');
        return button?.classList.contains('loading');
      };
      if (!working()) { this.release(); this.status(''); return; }
      this.status('Lägger till din studentskylt …');
      this.cartObserver?.disconnect();
      this.cartObserver = new MutationObserver(() => {
        if (!this.isConnected || !working()) {
          this.release();
          this.status('');
        }
      });
      // Dawn replaces sections and updates the button after both success and failure.
      this.cartObserver.observe(form.closest('product-info') || form.parentElement, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'aria-disabled']
      });
    }

    async submit(event) {
      const form = this.form();
      if (event.target !== form) return;
      if (this.allowForm === form) {
        this.allowForm = null;
        this.forwardedEvent = event;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy || this.themeLoading(form, event.submitter) || event.submitter?.disabled) return;
      this.error('');
      this.nameInput.value = this.nameInput.value.trim();
      this.classInput.value = this.classInput.value.trim();
      if (!this.photo || this.photoFile !== this.file.files[0]) {
        this.error('Välj ditt foto och vänta tills förhandsgranskningen är klar.');
        this.file.focus();
        return;
      }
      this.update();
      if (!this.validGeometry(this.geometry())) {
        this.error('Bildens placering kan inte beräknas. Centrera bilden igen.');
        return;
      }
      for (const input of [this.nameInput, this.classInput]) {
        if (!input.reportValidity()) return;
      }
      if (!form.reportValidity()) return;
      this.update();
      if (this.$('[data-recipe]').value.length > 255) {
        this.error('Designuppgifterna är för långa. Centrera bilden och försök igen eller kontakta butiken.');
        return;
      }
      const revision = this.revision, file = this.photoFile;
      this.lock();
      this.status('Sparar ditt foto och din förhandsgranskning …');
      try {
        // A successful original upload is reused if the preview upload needs a retry.
        const originalTask = this.originalSaved?.file === file
          ? Promise.resolve(this.originalSaved.url)
          : this.upload(file, file.type === 'image/png' ? 'original.png' : 'original.jpg').then(url => {
            if (this.isConnected && file === this.photoFile) this.originalSaved = {file, url};
            return url;
          });
        const previewTask = this.previewSaved?.revision === revision
          ? Promise.resolve(this.previewSaved.url)
          : this.renderJPEG(1000).then(blob => this.upload(blob, 'studentskylt-preview.jpg')).then(url => {
            if (this.isConnected && revision === this.revision) this.previewSaved = {revision, url};
            return url;
          });
        // Wait for both, including failures, before allowing an explicit retry.
        const outcomes = await Promise.allSettled([originalTask, previewTask]);
        if (!this.isConnected || revision !== this.revision || file !== this.photoFile) return;
        const failure = outcomes.find(result => result.status === 'rejected');
        if (failure) throw failure.reason;
        this.$('[data-original-property]').value = outcomes[0].value;
        this.$('[data-preview-property]').value = outcomes[1].value;
        this.$('[data-preview-link-property]').value = outcomes[1].value;
        const currentForm = this.form();
        if (!currentForm) throw new Error('Köpformuläret uppdaterades. Försök igen.');
        const submitter = event.submitter?.form === currentForm && event.submitter.isConnected
          ? event.submitter : currentForm.querySelector('[type="submit"][name="add"], button[type="submit"]');
        if (submitter?.disabled || this.themeLoading(currentForm, submitter) ||
            currentForm.querySelector('[name="id"]')?.disabled) {
          throw new Error('Det valda utförandet kan inte köpas just nu. Välj ett tillgängligt utförande.');
        }
        this.inert = false;
        if (!currentForm.reportValidity()) { this.release(); this.status(''); return; }
        this.forwardedEvent = null;
        this.allowForm = currentForm;
        // Exactly one pass into the theme's normal Ajax/native handling; no separate cart API call.
        try { currentForm.requestSubmit(submitter || undefined); }
        finally { this.allowForm = null; }
        if (!this.forwardedEvent) { this.release(); this.status(''); return; }
        this.inert = true;
        this.waitForTheme(currentForm, submitter);
      } catch (error) {
        if (!this.isConnected) return;
        this.release();
        this.status('');
        this.error(error.message || 'Designen kunde inte sparas. Försök igen.');
        this.$('[data-error]').focus();
      }
    }
  }
  customElements.define('sf-sign-editor', SignEditor);
})();
